import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Play, RefreshCw, Clock, Zap, Link, Hand, Crosshair } from 'lucide-react';
import {
  Badge,
  Button,
  Callout,
  Card,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FilterPills,
  QueryBoundary,
  Sheet,
  Toolbar,
  useToast,
} from '../../../components/ui/index.js';
import { Can } from '../../../components/auth/Can.js';
import { useDaemonScope } from './DaemonScopeContext.js';
import { daemonKeys, daemonReads, daemonWrites } from './daemon-queries.js';
import { useDaemonRunStream } from '../../../hooks/useDaemonRunStream.js';
import type { DaemonRecord, DaemonRunRecord } from '../../../generated/api/types.gen.js';
import { STATUS_VARIANTS, asKey, asText } from './run-audit-shared.js';
import { RunArtifactsPanel } from './RunArtifactsPanel.js';
import { ExecutionPolicyPanel } from './ExecutionPolicyPanel.js';
import { PermissionGrantsPanel } from './PermissionGrantsPanel.js';
import { RunAuditTabs } from './RunAuditTabs.js';

interface DaemonRunsTabProps {
  daemon: DaemonRecord;
}

const TRIGGER_DISPLAY: Record<string, { icon: React.ReactNode; label: string }> = {
  cron: { icon: <Clock size={12} className="text-accent-text" />, label: 'Cron' },
  schedule: { icon: <Clock size={12} className="text-accent-text" />, label: 'Schedule' },
  event: { icon: <Zap size={12} className="text-warning" />, label: 'Event' },
  chained: { icon: <Link size={12} className="text-success" />, label: 'Chained' },
  manual: { icon: <Hand size={12} className="text-muted" />, label: 'Manual' },
  dispatched: { icon: <Crosshair size={12} className="text-accent-text" />, label: 'Dispatched' },
};

/** Renders the triggeredBy value with an icon and label */
const TriggerLabel: React.FC<{ triggeredBy: string }> = ({ triggeredBy }) => {
  const key = triggeredBy.toLowerCase();
  const display = TRIGGER_DISPLAY[key];
  if (display) {
    return (
      <span className="flex items-center gap-1.5 text-sm text-muted">
        {display.icon}
        {display.label}
      </span>
    );
  }
  // Fallback: show raw value
  return <span className="text-sm text-muted">{triggeredBy}</span>;
};

const STATUS_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'completed', label: 'Completed' },
  { id: 'failed', label: 'Failed' },
  { id: 'skipped', label: 'Held or skipped' },
  { id: 'running', label: 'Running' },
  { id: 'pending', label: 'Pending' },
];

type RunStatus = NonNullable<NonNullable<Parameters<ReturnType<typeof daemonReads>['runs']>[1]>['status']>;

const sentence = (value: string | null | undefined) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : '—');

const RUN_COLUMNS: DataTableColumn<DaemonRunRecord>[] = [
  {
    key: 'status',
    header: 'Status',
    render: (run) => <Badge variant={STATUS_VARIANTS[asKey(run.status)] ?? 'neutral'}>{sentence(run.status)}</Badge>,
  },
  { key: 'trigger', header: 'Trigger', render: (run) => <TriggerLabel triggeredBy={run.triggeredBy ?? ''} /> },
  { key: 'findings', header: 'Findings', align: 'right', render: (run) => run.findingsCount ?? '—' },
  {
    key: 'duration',
    header: 'Duration',
    align: 'right',
    render: (run) => (run.durationMs != null ? `${Math.round(run.durationMs / 1000)}s` : '—'),
  },
  {
    key: 'quality',
    header: 'Quality',
    align: 'right',
    render: (run) => (run.qualityScore ? `${(parseFloat(run.qualityScore) * 100).toFixed(0)}%` : '—'),
  },
  {
    key: 'started',
    header: 'Started',
    align: 'right',
    render: (run) => (run.startedAt ? new Date(run.startedAt).toLocaleString() : '—'),
  },
];

const RunDetail: React.FC<{ run: DaemonRunRecord; daemonId: number }> = ({ run, daemonId }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-3 text-sm">
      <div>
        <span className="text-muted">Duration:</span>
        <span className="ml-1 text-body">{run.durationMs ? `${Math.round(run.durationMs / 1000)}s` : '—'}</span>
      </div>
      <div>
        <span className="text-muted">Findings:</span>
        <span className="ml-1 text-body">{run.findingsCount ?? 0}</span>
      </div>
      <div>
        <span className="text-muted">Errors:</span>
        <span className="ml-1 text-body">{run.errorsCount ?? 0}</span>
      </div>
      <div>
        <span className="text-muted">Quality:</span>
        <span className="ml-1 text-body">
          {run.qualityScore ? `${(parseFloat(run.qualityScore) * 100).toFixed(0)}%` : '—'}
        </span>
      </div>
    </div>

    {run.error ? <Callout tone={run.status === 'skipped' ? 'warning' : 'danger'}>{asText(run.error)}</Callout> : null}
    {run.reconciliationStatus === 'conflict' && (
      <Callout tone="warning">
        {asText(run.reconciliationReason) || 'Run state was reconciled from conflicting daemon/workflow events.'}
      </Callout>
    )}

    {/*
      QG-CLI-PAGES-006: these read `what_worked` / `what_failed` / `next_time`. The stored blob is snake_case, so the
      camelCase names were `undefined` and `.length` on them threw a TypeError the moment a run WITH a reflection was
      expanded.
    */}
    {run.reflection && (
      <div className="space-y-1 text-xs">
        {(run.reflection.what_worked?.length ?? 0) > 0 && (
          <div>
            <span className="font-medium text-success">Worked:</span> {run.reflection.what_worked?.join('; ')}
          </div>
        )}
        {(run.reflection.what_failed?.length ?? 0) > 0 && (
          <div>
            <span className="font-medium text-danger">Failed:</span> {run.reflection.what_failed?.join('; ')}
          </div>
        )}
        {(run.reflection.next_time?.length ?? 0) > 0 && (
          <div>
            <span className="font-medium text-warning">Next time:</span> {run.reflection.next_time?.join('; ')}
          </div>
        )}
      </div>
    )}

    <PermissionGrantsPanel daemonId={daemonId} />
    <ExecutionPolicyPanel daemonId={daemonId} runId={run.id} />
    <RunAuditTabs daemonId={daemonId} runId={run.id} />
    <RunArtifactsPanel daemonId={daemonId} runId={run.id} />
  </div>
);

export const DaemonRunsTab: React.FC<DaemonRunsTabProps> = ({ daemon }) => {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const { scope, apiBasePath, policy } = useDaemonScope();
  const [searchParams] = useSearchParams();
  const [openRunId, setOpenRunId] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const requestedRunId = Number(searchParams.get('runId'));

  useEffect(() => {
    if (Number.isFinite(requestedRunId) && requestedRunId > 0) setOpenRunId(requestedRunId);
  }, [requestedRunId]);

  const keys = daemonKeys(scope);
  const query = useQuery({
    ...daemonReads(scope).runs(daemon.id, statusFilter === 'all' ? undefined : { status: statusFilter as RunStatus }),
    refetchInterval: daemon.status === 'running' ? 3000 : undefined,
  });

  const runMutation = useMutation({
    ...daemonWrites(scope).run(),
    onSuccess: async () => {
      notify('Run started');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: keys.runs(daemon.id) }),
        queryClient.invalidateQueries({ queryKey: keys.get(daemon.id) }),
      ]);
    },
    onError: () => notify('Could not start a run', 'danger'),
  });

  const { events, connected } = useDaemonRunStream(daemon.status === 'running' ? daemon.id : null, apiBasePath);

  const runs: DaemonRunRecord[] = query.data?.data ?? [];
  const openRun = runs.find((run) => run.id === openRunId) ?? null;

  return (
    <div className="space-y-5">
      {events.length > 0 && (
        <Card title="Live events">
          <div className="max-h-32 space-y-1 overflow-y-auto">
            {events.slice(-10).map((evt) => (
              <div key={JSON.stringify(evt)} className="flex items-center gap-2 text-xs text-body">
                <Badge
                  variant={evt.event === 'completed' ? 'success' : evt.event === 'failed' ? 'danger' : 'info'}
                  size="sm"
                >
                  {evt.event}
                </Badge>
                <span className="text-muted">{JSON.stringify(evt).slice(0, 100)}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card>
        <Toolbar
          pills={
            <>
              <FilterPills
                options={STATUS_FILTERS}
                value={statusFilter}
                onChange={setStatusFilter}
                aria-label="Run status"
              />
              {connected && <Badge variant="success">Live</Badge>}
            </>
          }
          right={
            <>
              <Button
                variant="secondary"
                size="sm"
                iconOnly
                aria-label="Refresh runs"
                onClick={() => queryClient.invalidateQueries({ queryKey: keys.runs(daemon.id) })}
              >
                <RefreshCw size={14} />
              </Button>
              <Can policy={policy}>
                <Button
                  size="sm"
                  onClick={() => runMutation.mutate({ path: { id: String(daemon.id) } })}
                  disabled={!daemon.enabled}
                  loading={runMutation.isPending}
                >
                  <Play size={14} />
                  Run now
                </Button>
              </Can>
            </>
          }
        />
        <QueryBoundary query={query} label="runs" compact>
          {() => (
            <DataTable
              aria-label="Runs"
              columns={RUN_COLUMNS}
              rows={runs}
              rowKey={(run) => run.id}
              onRowClick={(run) => setOpenRunId(run.id)}
              pageSize={20}
              empty={
                <EmptyState icon={Play} title="No runs yet" description='Choose "Run now" to start a manual run.' />
              }
            />
          )}
        </QueryBoundary>
      </Card>

      <Sheet
        open={openRun !== null}
        onClose={() => setOpenRunId(null)}
        title={openRun ? `Run #${openRun.id}` : 'Run'}
        description={openRun?.startedAt ? new Date(openRun.startedAt).toLocaleString() : undefined}
        width="xl"
      >
        {openRun && <RunDetail run={openRun} daemonId={daemon.id} />}
      </Sheet>
    </div>
  );
};
